const pubUrl = process.env.PUBLIC_URL || "";

export const CASE_STUDIES = [
  {
    slug: "industrial-facility-neglected-repairs",
    type: "commercial",
    title: "Industrial Facility — Neglected Repairs",
    location: "Hamilton, ON",
    heroImg: `${pubUrl}/images/case-study/commercial-interior-damage/img1.jpg`,
    tags: ["Condition Review", "Repair Scope", "Inspection & Repairs"],
    facts: {
      buildingType: "Industrial Facility",
      roofType: "Standing Seam Metal",
      approxArea: "98,000 sqft",
      access: "Ladder",
      deliverable: "Inspection report + completed corrective work",
    },
    summary:
      "An interior inspection was performed following reports of active leaks and ceiling failures within a light industrial facility. The inspection identified multiple localized roof breaches contributing to interior water intrusion. Targeted repairs were completed to restore watertight integrity without expanding scope beyond documented deficiencies.",
    situation:
      "Facility personnel reported ongoing interior leaks and visible deterioration of ceiling finishes in several areas. Ceiling panels had failed or been removed, exposing insulation, framing, and utilities above. In multiple locations, daylight was visible from the interior, indicating direct openings through the roof assembly.",
    findings: [
      "Displaced and missing ceiling panels, allowing water intrusion to reach occupied spaces",
      "Exposed insulation and wire mesh with visible deterioration",
      "Rusting secondary framing members where moisture had been present over time",
      "Visible light penetration through the roof assembly, confirming active openings rather than condensation-related issues",
      "Utilities and conduit routed through affected areas, increasing risk if leaks continued",
    ],
    scope:
      "The scope of work was developed based strictly on observed and documented conditions. Repairs focused on addressing roof-level openings and compromised details aligned with interior leak locations, restoring watertight integrity at those areas, and eliminating visible light penetration. Work was limited to necessary corrective actions required to resolve active leakage without expanding scope to unaffected roof areas.",
    outcome:
      "Following completion of repairs, active leaks were eliminated at the identified locations and daylight penetration through the roof assembly was resolved. Interior spaces and overhead utilities were protected from continued moisture exposure, stabilizing affected areas and reducing the risk of further damage. Documentation from the inspection and completed repairs provided ownership with clear support for the repair-based solution and guidance for future planning.",
    photos: [
      `${pubUrl}/images/case-study/commercial-interior-damage/img1.jpg`,
      `${pubUrl}/images/case-study/commercial-interior-damage/img2.jpg`,
      `${pubUrl}/images/case-study/commercial-interior-damage/img3.jpg`,
      `${pubUrl}/images/case-study/commercial-interior-damage/img4.jpg`,
      `${pubUrl}/images/case-study/commercial-interior-damage/img5.jpg`,
      `${pubUrl}/images/case-study/commercial-interior-damage/img6.jpg`,
      `${pubUrl}/images/case-study/commercial-interior-damage/img7.jpg`,
    ],
  },
  {
    slug: "residential-roof-maintenance-and-caulking",
    type: "residential",
    title: "Residential Roof Maintenance and Caulking",
    location: "Barrie, ON",
    heroImg: `${pubUrl}/images/case-study/residential-roof-maintenance-and-caulking/img1.jpg`,
    tags: ["Maintenance", "Caulking", "Inspection & Repairs"],
    facts: {
      buildingType: "Residential Property",
      roofType: "Asphalt Shingle",
      approxArea: "N/A (multiple locations)",
      access: "Ladder",
      deliverable: "Completed corrective work",
    },
    summary:
      "Routine maintenance was performed to address deteriorated flashing details on a residential roof. The work focused on restoring the integrity of roof penetrations to prevent future water intrusion and extend the service life of the roofing system.",
    situation:
      "The homeowner requested maintenance after observing aging sealant and flashing conditions around roof penetrations. While no active interior leaks were reported at the time, the conditions presented an elevated risk for future water intrusion if left unaddressed.",
    findings: [
      "Aged and deteriorated sealant at vent pipe flashings",
      "Corroded fasteners and compromised flashing materials at roof penetrations",
      "Loose fasteners and compromised flashing transitions",
      "Early signs of moisture exposure around select flashing locations",
    ],
    scope:
      "The scope of work was developed based strictly on observed and documented conditions. Repairs focused on addressing roof-level openings and compromised details, restoring watertight integrity at those areas. Work was limited to necessary corrective actions required to resolve active leakage without expanding scope to unaffected roof areas.",
    outcome:
      "Following completion of repairs, active leaks were eliminated at the identified locations and the roof condition was improved. Interior spaces and overhead utilities were protected from continued moisture exposure, stabilizing affected areas and reducing the risk of further damage. Documentation from the inspection and completed repairs provided ownership with clear support for the repair-based solution and guidance for future planning.",
    photos: [
      `${pubUrl}/images/case-study/residential-roof-maintenance-and-caulking/img1.jpg`,
      `${pubUrl}/images/case-study/residential-roof-maintenance-and-caulking/img2.jpg`,
      `${pubUrl}/images/case-study/residential-roof-maintenance-and-caulking/img3.jpg`,
      `${pubUrl}/images/case-study/residential-roof-maintenance-and-caulking/img4.jpg`,
      `${pubUrl}/images/case-study/residential-roof-maintenance-and-caulking/img5.jpg`,
      `${pubUrl}/images/case-study/residential-roof-maintenance-and-caulking/img6.jpg`,
      `${pubUrl}/images/case-study/residential-roof-maintenance-and-caulking/img7.jpg`,
      `${pubUrl}/images/case-study/residential-roof-maintenance-and-caulking/img8.jpg`,
      `${pubUrl}/images/case-study/residential-roof-maintenance-and-caulking/img9.jpg`,
      `${pubUrl}/images/case-study/residential-roof-maintenance-and-caulking/img10.jpg`,
      `${pubUrl}/images/case-study/residential-roof-maintenance-and-caulking/img11.jpg`,
      `${pubUrl}/images/case-study/residential-roof-maintenance-and-caulking/img12.jpg`,
      `${pubUrl}/images/case-study/residential-roof-maintenance-and-caulking/img13.jpg`,
      `${pubUrl}/images/case-study/residential-roof-maintenance-and-caulking/img14.jpg`,
      `${pubUrl}/images/case-study/residential-roof-maintenance-and-caulking/img15.jpg`,
    ],
  },
  {
    slug: "commercial-curb-leaks",
    type: "commercial",
    title: "Multi-Tenant Commercial Building — HVAC Curb Leak Investigation",
    location: "Scarborough, ON",
    heroImg: `${pubUrl}/images/case-study/commercial-curb-leaks/img1.jpg`,
    tags: ["Leak Tracing", "Inspection Only"],
    facts: {
      buildingType: "Multi-Tenant Commercial",
      roofType: "Flat membrane roof",
      approxArea: "12,000 sqft",
      access: "Ladder",
      deliverable: "Inspection report + repair recommendations",
    },
    summary:
      "An inspection was conducted after multiple tenants reported active leaks during rain events. Interior reports indicated recurring water intrusion affecting separate tenant spaces. The inspection identified compromised HVAC curb details as the primary source of leakage. Findings and repair recommendations were documented in a formal inspection report provided to ownership.",
    situation:
      "Tenants within a multi-tenant commercial building experienced active leaks during rainfall, raising concerns regarding roof performance and potential liability across multiple occupied spaces. Leakage was reported beneath rooftop mechanical equipment serving different tenant areas. Ownership required identification of the leak source and confirmation of whether the issues were isolated to specific details or indicative of broader system failure.",
    findings: [
      "Deteriorated and improperly sealed HVAC curb flashing assemblies",
      "Failed or aged sealant at curb transitions and accessory penetrations",
      "Leak locations aligned with rooftop mechanical equipment serving affected tenant spaces",
      "Roof field membrane generally serviceable outside of curb areas",
      "No evidence of widespread membrane failure beyond mechanical penetrations",
    ],
    scope:
      "The scope of services was limited to inspection, documentation, and reporting. This included evaluation of rooftop HVAC curb conditions, correlation of interior leak locations with roof-level details, photographic documentation, and development of repair recommendations. No repair work was performed as part of this engagement.",
    outcome:
      "The inspection report provided ownership with clear identification of the leak source and recommended corrective actions. Based on the findings, the owner elected to carry out repairs independently. Documentation from the inspection supported informed decision-making and resolution planning without requiring system-wide replacement.",
    photos: [
      `${pubUrl}/images/case-study/commercial-curb-leaks/img1.jpg`,
      `${pubUrl}/images/case-study/commercial-curb-leaks/img2.jpg`,
    ],
  },
  {
    slug: "commercial-inspection-only",
    type: "commercial",
    title: "Industrial Facility — Interior Leak Investigation",
    location: "St Catherine's, ON",
    heroImg: `${pubUrl}/images/case-study/commercial-inspection-only/img1.jpg`,
    tags: ["Leak Investigation", "Roof Accessories", "Inspection Only"],
    facts: {
      buildingType: "Industrial",
      roofType: "Standing Seam Metal",
      approxArea: "21,000 sqft",
      access: "Ladder",
      deliverable: "Inspection report with findings and recommendations",
    },
    summary:
      "An inspection was performed to investigate active interior leaks reported within an industrial facility. The evaluation focused on correlating interior leak locations with roof-level conditions. The inspection determined that water intrusion was originating from aged and deteriorated flashing at roof accessories. Findings and recommendations were documented in a formal inspection report provided to ownership.",
    situation:
      "Facility personnel reported active water intrusion occurring during rain events, with leaks visible inside the building beneath roof framing and accessory locations. The leaks raised concerns regarding ongoing interior damage and potential impact to operations. Ownership requested an inspection to identify the source of leakage and determine whether the issues were isolated or indicative of broader roof system failure.",
    findings: [
      "Evidence of active water intrusion visible from the interior during and after rain events",
      "Staining and moisture patterns aligned with roof accessory locations",
      "Aged and deteriorated flashing at roof-mounted accessories",
      "Gaps and failed transitions at accessory flashing interfaces",
      "No evidence of widespread failure within the primary roof field membrane",
    ],
    scope:
      "The scope of services was limited to inspection and documentation. This included interior observation of leak conditions, rooftop evaluation of roof accessories and flashing, photographic documentation, and correlation of interior leak locations with roof-level details. No repair work was performed as part of this engagement.",
    outcome:
      "The inspection report provided ownership with clear identification of the leak source and contributing conditions. Documentation confirmed that interior leaks were caused by aged flashing at roof accessories rather than failure of the primary roof field. Based on the findings, ownership was able to evaluate repair options and plan corrective action independently.",
    photos: [
      `${pubUrl}/images/case-study/commercial-inspection-only/img1.jpg`,
      `${pubUrl}/images/case-study/commercial-inspection-only/img2.jpg`,
      `${pubUrl}/images/case-study/commercial-inspection-only/img3.jpg`,
      `${pubUrl}/images/case-study/commercial-inspection-only/img4.jpg`,
      `${pubUrl}/images/case-study/commercial-inspection-only/img5.jpg`,
      `${pubUrl}/images/case-study/commercial-inspection-only/img6.jpg`,
      `${pubUrl}/images/case-study/commercial-inspection-only/img7.jpg`,
    ],
  },
  {
    slug: "commercial-skylights-fasteners",
    type: "commercial",
    title: "Commercial Metal Roof — Remaining Service Life Evaluation and Targeted Repairs",
    location: "Mississauga, ON",
    heroImg: `${pubUrl}/images/case-study/commercial-skylights-fasteners/img1.jpg`,
    tags: ["Lifecycle Assessment", "Metal Roof", "Targeted Repairs"],
    facts: {
      buildingType: "Commercial / Industrial",
      roofType: "Exposed-fastener metal roof with fiberglass skylights",
      approxArea: "38,000 sqft",
      access: "Ladder",
      deliverable: "Inspection report + completed corrective work",
    },
    summary:
      "An inspection was performed to evaluate the remaining service life of a commercial metal roof system. The assessment identified localized deterioration affecting roof vent flashings and multiple fiberglass skylight sections on approximately one half of the roof. Based on observed conditions, targeted corrective work was completed to address active deficiencies and extend the remaining service life of affected components.",
    situation:
      "Ownership requested an evaluation of roof condition and remaining lifespan to determine whether immediate replacement was required or if interim measures could be taken. Visual indicators suggested localized aging rather than uniform system failure. The inspection focused on identifying deteriorated components contributing to accelerated wear and potential water intrusion.",
    findings: [
      "Advanced corrosion at roof vent flashing assemblies on one half of the roof",
      "Corroded and deteriorated fasteners securing vent flashing components",
      "Multiple fiberglass skylight sections with corroded fasteners",
      "Cracking observed in select fiberglass skylight panels",
      "Primary roof panels generally serviceable outside of affected areas",
    ],
    scope:
      "The scope of work included inspection and limited corrective measures at identified deficient components. Corroded fasteners at roof vent flashings and fiberglass skylights were removed and replaced. A silicone coating was applied over repaired areas to improve weather resistance and extend service life. Select cracked fiberglass skylight panels were reinforced using fiberglass mesh embedded in silicone coating. Work was limited to documented areas of deterioration.",
    outcome:
      "Targeted repairs stabilized deteriorated components and reduced the risk of ongoing water intrusion at affected areas. The applied measures extended the remaining service life of roof vent flashings and fiberglass skylights without requiring broader system replacement. Documentation from the inspection and completed work provided ownership with a clear understanding of roof condition and future planning considerations.",
    photos: [
      `${pubUrl}/images/case-study/commercial-skylights-fasteners/img1.jpg`,
      `${pubUrl}/images/case-study/commercial-skylights-fasteners/img2.jpg`,
      `${pubUrl}/images/case-study/commercial-skylights-fasteners/img3.jpg`,
      `${pubUrl}/images/case-study/commercial-skylights-fasteners/img4.jpg`,
      `${pubUrl}/images/case-study/commercial-skylights-fasteners/img5.jpg`,
      `${pubUrl}/images/case-study/commercial-skylights-fasteners/img6.jpg`,
      `${pubUrl}/images/case-study/commercial-skylights-fasteners/img7.jpg`,
      `${pubUrl}/images/case-study/commercial-skylights-fasteners/img8.jpg`,
    ],
  },
  {
    slug: "emergency-residential-roof-breach-full-repair",
    type: "residential",
    title: "Residential Property — Emergency Roof Breach Repair",
    location: "Toronto, ON",
    heroImg: `${pubUrl}/images/case-study/residential-emergency/img1.jpg`,
    tags: ["Emergency Repair", "After-Hours Work", "Water Intrusion Prevention"],
    facts: {
      buildingType: "Single-family residential",
      roofType: "Asphalt shingle roof",
      approxArea: "2,400 sqft",
      access: "Ladder",
      deliverable: "Emergency response and permanent roof repair",
    },
    summary:
      "An emergency service call was received after a roof breach created an active opening into a residential structure. With heavy rainfall forecasted, immediate corrective action was required to prevent interior damage. Crews were dispatched late in the afternoon and worked into the evening to complete a proper repair and restore the roof’s weather resistance.",
    situation:
      "The homeowner reported a newly formed hole in the roof assembly with exposed decking and surrounding damage to roofing materials. Weather forecasts indicated significant rainfall overnight, creating an immediate risk of interior water intrusion. Due to the severity and timing of the damage, same-day emergency response was required.",
    findings: [
      "Active breach through roofing materials exposing underlying roof deck",
      "Damaged shingles and underlayment surrounding the opening",
      "Displaced and compromised roofing materials at the affected area",
      "High risk of interior water intrusion due to imminent rainfall",
    ],
    scope:
      "The scope of work included removal of damaged roofing materials, evaluation of the exposed deck, and installation of replacement underlayment and shingles to restore the integrity of the roof system. Repairs were completed using proper materials and installation methods despite limited daylight conditions. Work continued into the evening to ensure the roof was fully repaired and weather-tight prior to rainfall.",
    outcome:
      "The roof was fully repaired before the onset of adverse weather conditions, preventing interior water intrusion. The affected area was restored to proper condition, returning the roof to a weather-resistant state. Documentation of the repair was provided to the homeowner along with guidance for any future roof maintenance considerations.",
    photos: [
      `${pubUrl}/images/case-study/residential-emergency/img1.jpg`,
      `${pubUrl}/images/case-study/residential-emergency/img2.jpg`,
      `${pubUrl}/images/case-study/residential-emergency/img3.jpg`,
    ],
  },
  {
    slug: "residential-roof-flashing-routine-maintenance",
    type: "residential",
    title: "Residential Property — Roof Flashing Maintenance",
    location: "North York, ON",
    heroImg: `${pubUrl}/images/case-study/residential-maintenance/img1.jpg`,
    tags: ["Routine Maintenance", "Flashing Repairs", "Preventive Work"],
    facts: {
      buildingType: "Single-family residential",
      roofType: "Asphalt shingle roof",
      approxArea: "N/A (multiple flashing locations)",
      access: "Rooftop access",
      deliverable: "Routine maintenance and flashing repairs",
    },
    summary:
      "Routine maintenance was performed to address deteriorated flashing details on a residential roof. The work focused on restoring the integrity of roof penetrations and wall transitions to prevent future water intrusion and extend the service life of the roofing system.",
    situation:
      "The homeowner requested maintenance after observing aging sealant and flashing conditions around roof penetrations and wall interfaces. While no active interior leaks were reported at the time, the conditions presented an elevated risk for future water intrusion if left unaddressed.",
    findings: [
      "Aged and deteriorated sealant at vent pipe flashings",
      "Improper or fatigued flashing materials at roof penetrations",
      "Loose fasteners and compromised flashing transitions",
      "Early signs of moisture exposure around select flashing locations",
    ],
    scope:
      "The scope of work included removal of deteriorated sealant and localized flashing components as required to properly access affected areas. Flashing details were repaired and resealed using appropriate materials and installation methods to restore watertight transitions at roof penetrations and adjacent wall interfaces.",
    outcome:
      "Flashing details were restored to proper condition, reducing the risk of future leaks and improving the overall durability of the roof system. Preventive maintenance addressed vulnerable areas before failure occurred, extending service life and providing the homeowner with improved long-term performance.",
    photos: [
      `${pubUrl}/images/case-study/residential-maintenance/img1.jpg`,
      `${pubUrl}/images/case-study/residential-maintenance/img2.jpg`,
      `${pubUrl}/images/case-study/residential-maintenance/img3.jpg`,
      `${pubUrl}/images/case-study/residential-maintenance/img4.jpg`,
      `${pubUrl}/images/case-study/residential-maintenance/img5.jpg`,
      `${pubUrl}/images/case-study/residential-maintenance/img6.jpg`,
      `${pubUrl}/images/case-study/residential-maintenance/img7.jpg`,
    ],
  },
  {
    slug: "aged-asphalt-shingle-wind-damage-assessment",
    type: "residential",
    title: "Residential Property — Old Roof with Wind Damage",
    location: "Pickering, ON",
    heroImg: `${pubUrl}/images/case-study/residential-wind-damage/img1.jpg`,
    tags: ["Residential", "Inspection", "Wind Damage", "Asphalt Shingles"],
    facts: {
      buildingType: "Single-family residence",
      roofType: "Asphalt shingle",
      approxArea: "2,000 sq ft",
      access: "Ladder",
      deliverable: "Inspection findings and repair recommendations",
    },
    summary:
      "A residential asphalt shingle roof inspection performed after a mild storm revealed wind damage across multiple areas of an aging roofing system, along with installation defects on a newer roof section that increased overall vulnerability.",
    situation:
      "The homeowner requested an evaluation following a mild storm that caused visible shingle displacement and exposed areas on the roof. While storm-related damage was present, the roofing system was already at or beyond its expected service life, prompting a broader assessment of overall roof condition.",
    findings: [
      "Multiple areas of wind-lifted and missing shingles across the primary roof field",
      "Advanced age-related wear, including granule loss and brittle shingles",
      "Improperly installed ridge section on a newer portion of the roof",
      "Evidence suggesting the ridge was installed without properly integrating with the existing aged shingles",
      "Previous localized repairs inconsistent with standard installation practices",
    ],
    scope:
      "Our scope consisted of a full visual roof inspection to document storm-related damage, age-related deterioration, and prior installation deficiencies. Key transition areas and ridge conditions were closely evaluated, and findings were documented to support informed decision-making regarding potential repairs or replacement.",
    outcome:
      "The homeowner was provided with a detailed inspection summary and repair recommendations addressing both storm damage and underlying system deficiencies. After reviewing the findings, the homeowner elected to decline all proposed repair options at this time and requested documentation for their records.",
    photos: [
      `${pubUrl}/images/case-study/residential-wind-damage/img1.jpg`,
      `${pubUrl}/images/case-study/residential-wind-damage/img2.jpg`,
      `${pubUrl}/images/case-study/residential-wind-damage/img3.jpg`,
      `${pubUrl}/images/case-study/residential-wind-damage/img4.jpg`,
      `${pubUrl}/images/case-study/residential-wind-damage/img5.jpg`,
    ],
  },
];